import { Icons, ReactIcons } from "@kalyzee/escaet-ui";
import { Meeting, MeetingRoom, MeetingRoomHighlightingState, MeetingRoomStatus, MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useEffect, useMemo } from "react";

import { MeetingSessionOvenMediaReceiverManagerBaseData, MeetingSourceElement } from "@kalyzee/kast-web-sdk";
import { useStateWithRef } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import MeetingSessionPlayer from "../../../../components/player/MeetingSessionPlayer";
import { SecondaryScreenSessions } from "../../../../components/secondary-screens/sessions/Sessions";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { useMeetingVideoFeedbackSource } from "../../../../hooks/meeting";
import { ScreenParams } from "../../../../hooks/screen";
import { DEFAULT_GROUP_PANEL_WIDTH, DEFAULT_PANEL_WIDTH, SlaveCarouselData } from "../meeting";
import styles from "./screen.module.css";

const useGroupRequest = (rooms?: MeetingRoom[]) => {
  const [requests, setRequests, requestsRef] = useStateWithRef<{ type: "highlight" | "callingModerator"; room: MeetingRoom }[]>([]);
  const groupHighlighRequests = useMemo(
    () => rooms?.filter((r) => r.status !== MeetingRoomStatus.Closed && r.highlighting === MeetingRoomHighlightingState.PENDING_REQUEST) ?? [],
    [rooms]
  );
  const groupModeratorRequests = useMemo(() => rooms?.filter((r) => r.status !== MeetingRoomStatus.Closed && r.callingModerator) ?? [], [rooms]);

  useEffect(() => {
    let update = false;
    const newRequests: { type: "highlight" | "callingModerator"; room: MeetingRoom }[] = [];
    requestsRef.current.forEach((r) => {
      if (r.type === "highlight") {
        if (groupHighlighRequests.find((r2) => r.room.id === r2.id)) {
          newRequests.push(r);
        } else {
          update = true;
        }
      } else if (r.type === "callingModerator") {
        if (groupModeratorRequests.find((r2) => r.room.id === r2.id)) {
          newRequests.push(r);
        } else {
          update = true;
        }
      }
    });

    groupHighlighRequests.forEach((r) => {
      if (!requestsRef.current.find((r2) => r2.room.id === r.id)) {
        newRequests.push({ type: "highlight", room: r });
        update = true;
      }
    });

    groupModeratorRequests.forEach((r) => {
      if (!requestsRef.current.find((r2) => r2.room.id === r.id)) {
        newRequests.push({ type: "callingModerator", room: r });
        update = true;
      }
    });

    if (update) setRequests(newRequests);
  }, [groupHighlighRequests, groupModeratorRequests]);

  return requests;
};

export interface SecondaryScreen2Props {
  meeting: Meeting;
  videoSessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[];
  screenParams: ScreenParams;
  carouselData: SlaveCarouselData;
  rooms?: MeetingRoom[];
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export interface SecondaryScreen2Ref {}

export const SecondaryScreen2 = React.forwardRef(
  (
    { meeting, videoSessions, screenParams, carouselData, rooms, children, className, style }: SecondaryScreen2Props,
    forwardRef: ForwardedRef<SecondaryScreen2Ref>
  ) => {
    // Verifying expiration on slave side
    const groupRequests = useGroupRequest(rooms);
    const videoFeedbackSource = useMeetingVideoFeedbackSource();

    const highlightRequests = useMemo(
      () => meeting?.sessions?.filter((s) => s.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) ?? [],
      [meeting]
    );

    const renderVideoFeedback = () => {
      if (!videoFeedbackSource) return null;
      return <MeetingSourceElement className={styles.videoFeedback} source={videoFeedbackSource} using audioContext={getAudioContextForVolumeAnalyzer()} />;
    };

    const renderLeftPanel = () => {
      let items = 0;
      let slots = 5;
      const nodes: React.ReactNode[] = [];
      const videoFeedback = videoFeedbackSource ? true : false;
      if (carouselData.data.mode === "groups") {
        items = groupRequests.length;
        slots = videoFeedback ? 4 : 6;
        for (let i = 0; i < slots; i++) {
          const request = i < groupRequests.length ? groupRequests[i] : undefined;
          if (request?.type === "callingModerator") {
            nodes.push(
              <div key={`room-expert-request-${request.room.id}`} className={styles.groupModeratorRequestContainer}>
                <ReactIcons.WhiteArrowRight style={{ width: 40, height: 40 }} />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div className={styles.groupNameContainer}>
                    Groupe
                    <div className={styles.groupName}>{request.room.name?.toUpperCase()}</div>
                  </div>
                  <div style={{ fontSize: 18, lineHeight: "21px" }}>Le groupe vous appelle</div>
                </div>
              </div>
            );
          } else if (request?.type === "highlight") {
            nodes.push(
              <div key={`hightlight-request-${request.room.id}`} className={styles.groupHighlightRequestContainer}>
                <ReactIcons.Tableau style={{ width: 40, height: 40 }} />
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div className={styles.groupNameContainer}>
                    Groupe
                    <div className={styles.groupName}>{request.room.name?.toUpperCase()}</div>
                  </div>
                  <div style={{ fontSize: 18, lineHeight: "21px" }}>Souhaite monter au tableau</div>
                </div>
              </div>
            );
          }
        }
      } else {
        items = highlightRequests.length;
        slots = videoFeedback ? 2 : 3;
        for (let i = 0; i < slots; i++) {
          const session = i < highlightRequests.length ? highlightRequests[i] : undefined;
          if (session) {
            nodes.push(
              <MeetingSessionPlayer
                key={`highlighting_session_${session.id}`}
                className={styles.player}
                meeting={meeting}
                sessionId={session.id}
                ovenMediaSessions={videoSessions.filter((s) => s.data.session.id === session.id)}
                muted
              />
            );
          }
        }
      }

      for (let i = nodes.length; i < slots; i++) {
        nodes.push(
          <div key={`highlighting_session_undefined_${i}`} className={styles.emptyContainer}>
            <img src={Icons.FingerGrey} alt="hand" style={{ width: "40px", height: "40px", objectFit: "contain", opacity: "0.3" }} />
          </div>
        );
      }

      return (
        <div className={[styles.column, styles.leftPanel].join(" ")} style={{ width: "100%" }}>
          <div className={styles.highlighting}>
            <div className={styles.title}>{"Demandes de participation"}</div>
            <div className={styles.sessionContainer}>{nodes}</div>
            <div className={styles.details}>
              {items > slots ? `+${items - slots} demandes ` : items > 1 ? `${items} demandes ` : `${items} demande `}
              <img src={Icons.FingerGrey} alt="hand" style={{ width: "30px", height: "30px", objectFit: "contain", opacity: "1" }} />
            </div>
          </div>
          {videoFeedback ? renderVideoFeedback() : null}
        </div>
      );
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    const classes = [];
    if (className) classes.push(className);
    return (
      <div className={classes.join(" ")} style={style}>
        <div style={{ width: screenParams.panelWidth ?? (carouselData.data.mode === "groups" ? DEFAULT_GROUP_PANEL_WIDTH : DEFAULT_PANEL_WIDTH), height: "100%" }}>
          {renderLeftPanel()}
        </div>
        <SecondaryScreenSessions meeting={meeting} videoSessions={videoSessions} screenParams={screenParams} carouselData={carouselData} />
        {children}
      </div>
    );
  }
);
